// useFinanceSettings hook — Task 23
//
// Provides company-wide finance settings state with dirty-tracking and save/reset helpers.
// Fetches from the server-side API on mount and persists via PUT /api/settings/finance.

import { useState, useCallback, useEffect } from 'react';
import {
  type FinanceSettings,
  DEFAULT_FINANCE_SETTINGS,
  getFinanceSettings,
  fetchFinanceSettings,
  saveFinanceSettings,
} from '@/lib/finance-settings-storage';
import type { GpCalculationMethod } from '@/lib/gp-calculation';

interface UseFinanceSettingsReturn {
  settings: FinanceSettings;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  updateSetting: <K extends keyof FinanceSettings>(key: K, value: FinanceSettings[K]) => void;
  saveSettings: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useFinanceSettings(): UseFinanceSettingsReturn {
  const [settings, setSettings] = useState<FinanceSettings>(() => getFinanceSettings());
  const [savedSettings, setSavedSettings] = useState<FinanceSettings>(() => getFinanceSettings());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchFinanceSettings().then((fetched) => {
      if (!cancelled) {
        setSettings(fetched);
        setSavedSettings(fetched);
        setIsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges =
    settings.gpCalculationMethod !== savedSettings.gpCalculationMethod ||
    settings.revenueDisplayMode !== savedSettings.revenueDisplayMode;

  const updateSetting = useCallback(
    <K extends keyof FinanceSettings>(key: K, value: FinanceSettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveSettingsHandler = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveFinanceSettings(settings);
      setSavedSettings({ ...settings });
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  const resetToDefaults = useCallback(() => {
    setSettings({ ...DEFAULT_FINANCE_SETTINGS });
  }, []);

  // Convenience typed helper (not exported on the interface — used internally only)
  void (updateSetting as (key: 'gpCalculationMethod', value: GpCalculationMethod) => void);

  return {
    settings,
    hasUnsavedChanges,
    isSaving,
    isLoading,
    updateSetting,
    saveSettings: saveSettingsHandler,
    resetToDefaults,
  };
}
